/**
 * 시스템 설정 — 「고객센터 안내」 탭(EA-70)·판 보기(EA-71)·새 판(EA-72).
 *
 * 요구: 거래 흐름 04 8단계 — 사장님 원문 8 「사용자가 직접 거래소의 고객센터로 연락한다」, 2026-10-01
 * 「거래소 관리자 약관처럼 거래소관리자에서 관리 할 수 있도록 만들어라」, 「시작해」. 지갑거래서명약관
 * (EA-65~EA-67)과 같은 방식이다 — 고치면 새 판, 옛 판은 지우지 않는다. 판마다 한국어·영어로 연락처
 * (전화번호·채널)·운영 시간·안내 글이 있다.
 *
 * 계약: XM-106(목록)·XM-107(시행 중)·XM-108(한 판) — 키 `terms.view`, XM-109(새 판) — 키 `terms.edit`
 * (거래소백엔드 `CustomerCenterGuideController`, 912775e).
 *
 * 거래소 앱은 XA-69 로 시행 중인 판(XM-107 과 같은 판)을 받아 일반회원의 사고 팝업, 사고처리 중인 거래가
 * 있는 콜대기자의 화면(XA-70), 앱 메뉴에 보인다 — 이 웹은 그 길에 끼지 않는다. **처음에는 판이 없다**
 * (V522 는 판을 넣지 않음) — 판이 생기기 전에는 앱에 안내가 보이지 않는다(XA-69 `guide` null).
 */
import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  createCustomerCenterGuide, getCustomerCenterGuide, getEffectiveCustomerCenterGuide, listCustomerCenterGuides,
} from '@/api/admin'
import { useSession } from '@/auth/session'
import { setFlash } from '@/auth/pendingWrite'
import { AdminRef, Badge, Button, Field, Notice, Row, TD, TextArea, TH, Time } from '@/components/ui'
import { Confirm, submitWrite, useWrite, ViewDialog } from '@/components/Confirm'
import { QueryState } from '@/components/QueryState'
import { effectiveMillis, LangText, orNullOn404, Plain } from '@/components/Versioned'
import { DateTimeField } from '@/components/DateFields'
import type { CustomerCenterGuide } from '@/api/types'

/** 탭·창 이름 — 거래 흐름 04 8단계(합의한 이름). */
export const CUSTOMER_CENTER_GUIDE = 'Customer center guide'

/** 어디에 보이는지(탭 안내·새 판 창). */
const WHERE = 'From the effective time, the exchange app shows this version as its customer center guide (the incident popup of general members, the screen of members waiting for calls who have a trade in incident handling, and the app menu).'

const effectiveOrNull = () => orNullOn404(getEffectiveCustomerCenterGuide)

/** EA-70 — 고객센터 안내 탭(XM-106·XM-107). */
export function CustomerCenterGuideTab() {
  const { has } = useSession()
  const list = useQuery({ queryKey: ['customer-center-guides'], queryFn: listCustomerCenterGuides })
  const effective = useQuery({ queryKey: ['customer-center-guides', 'effective'], queryFn: effectiveOrNull })
  const [viewing, setViewing] = useState<number | null>(null)
  const [creating, setCreating] = useState(false)
  // 판 새것부터(서버도 새 판 먼저 준다).
  const rows = [...(list.data ?? [])].sort((a, b) => b.version - a.version)
  const current = effective.data?.version ?? null

  return (
    <>
      <p className="mb-3 text-xs text-slate-500">
        How members contact the customer center. Each version has a contact, hours and a guide text in Korean and English. {WHERE}
        {' '}Without a version, the app shows no guide.
      </p>
      {has('terms.edit') && (
        <div className="mb-4"><Button onClick={() => setCreating(true)}>New version</Button></div>
      )}
      <QueryState isPending={list.isPending} error={list.error} empty={rows.length === 0}
        emptyText="There is no customer center guide.">
        <table className="w-full text-sm">
          <thead><tr>
            <th className={TH}>Version</th><th className={TH}>Korean contact</th><th className={TH}>Korean hours</th>
            <th className={TH}>English contact</th><th className={TH}>English hours</th>
            <th className={TH}>Effective at</th><th className={TH}>Created by</th><th className={TH}>Created at</th>
          </tr></thead>
          <tbody>
            {rows.map((g) => (
              <tr key={g.version}>
                <td className={TD}>
                  <button type="button" className="text-teal-700 underline dark:text-teal-400"
                    onClick={() => setViewing(g.version)}>v{g.version}</button>
                  {current === g.version && <Badge tone="ok">In effect</Badge>}
                </td>
                <td className={TD}>{g.contactKo}</td>
                <td className={TD}>{g.hoursKo}</td>
                <td className={TD}>{g.contactEn}</td>
                <td className={TD}>{g.hoursEn}</td>
                <td className={TD}><Time ms={g.effectiveAt} /></td>
                <td className={TD}><AdminRef adminId={g.createdByAdminId} empty="System" /></td>
                <td className={TD}><Time ms={g.createdAt} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </QueryState>
      {viewing !== null && (
        <ViewGuideDialog version={viewing} effective={current} onClose={() => setViewing(null)} />
      )}
      {creating && <NewGuideDialog base={effective.data ?? null} onClose={() => setCreating(false)} />}
    </>
  )
}

/** 한 언어의 연락처·운영 시간·안내 글 — 글로만(HTML 로 그리지 않는다). */
function GuideText({ lang, contact, hours, body }: { lang: string; contact: string; hours: string; body: string }) {
  return (
    <LangText lang={lang}>
      <dl className="mb-2 grid grid-cols-[6rem_1fr] gap-1.5">
        <Row label="Contact"><Plain text={contact} /></Row>
        <Row label="Hours"><Plain text={hours} /></Row>
      </dl>
      <Plain text={body} />
    </LangText>
  )
}

/** EA-71 — 판 보기(XM-108). 한국어·영어를 함께. */
function ViewGuideDialog({ version, effective, onClose }: {
  version: number
  effective: number | null
  onClose: () => void
}) {
  const q = useQuery({ queryKey: ['customer-center-guides', version], queryFn: () => getCustomerCenterGuide(version) })
  const g = q.data
  return (
    <ViewDialog open title={`${CUSTOMER_CENTER_GUIDE} v${version}`} onClose={onClose}>
      <QueryState isPending={q.isPending} error={q.error}>
        {g && (
          <>
            <dl className="mb-3 grid grid-cols-[6rem_1fr] gap-1.5">
              <Row label="Effective at">
                <Time ms={g.effectiveAt} />
                {effective === g.version && <Badge tone="ok">In effect</Badge>}
              </Row>
              <Row label="Created by"><AdminRef adminId={g.createdByAdminId} empty="System" /></Row>
              <Row label="Created at"><Time ms={g.createdAt} /></Row>
            </dl>
            <GuideText lang="Korean" contact={g.contactKo} hours={g.hoursKo} body={g.bodyKo} />
            <GuideText lang="English" contact={g.contactEn} hours={g.hoursEn} body={g.bodyEn} />
          </>
        )}
      </QueryState>
    </ViewDialog>
  )
}

/** EA-72 — 새 판(XM-109). 처음 값은 시행 중 판(없으면 빈 칸 — EA-67 과 같게). */
function NewGuideDialog({ base, onClose }: { base: CustomerCenterGuide | null; onClose: () => void }) {
  const qc = useQueryClient()
  const w = useWrite()
  const [contactKo, setContactKo] = useState(base?.contactKo ?? '')
  const [hoursKo, setHoursKo] = useState(base?.hoursKo ?? '')
  const [bodyKo, setBodyKo] = useState(base?.bodyKo ?? '')
  const [contactEn, setContactEn] = useState(base?.contactEn ?? '')
  const [hoursEn, setHoursEn] = useState(base?.hoursEn ?? '')
  const [bodyEn, setBodyEn] = useState(base?.bodyEn ?? '')
  const [effectiveAt, setEffectiveAt] = useState('')

  const { ms: effectiveMs, bad: badDate } = effectiveMillis(effectiveAt)
  // 여섯 칸 모두 필수(서버 @NotBlank — 공백만이면 400).
  const blank = [contactKo, hoursKo, bodyKo, contactEn, hoursEn, bodyEn].some((s) => s.trim() === '')

  async function submit() {
    const send = async () => {
      const g = await createCustomerCenterGuide({ contactKo, hoursKo, bodyKo, contactEn, hoursEn, bodyEn }, effectiveMs)
      setFlash({ tone: 'ok', text: `Created ${CUSTOMER_CENTER_GUIDE} v${g.version}.` })
      await qc.invalidateQueries({ queryKey: ['customer-center-guides'] })
    }
    await submitWrite(w, `${CUSTOMER_CENTER_GUIDE} — new version`, send, onClose)
  }

  return (
    <Confirm open wide title={`${CUSTOMER_CENTER_GUIDE} — new version`} action="Create" danger
      busy={w.busy} error={w.error} onCancel={onClose} onConfirm={() => { void submit() }}
      disabled={blank || badDate}>
      <div className="flex flex-col gap-3">
        <Field label="Korean contact (required · 200 chars)" value={contactKo} maxLength={200}
          onChange={(e) => setContactKo(e.target.value)} />
        <Field label="Korean hours (required · 200 chars)" value={hoursKo} maxLength={200}
          onChange={(e) => setHoursKo(e.target.value)} />
        <TextArea label="Korean guide text (required · 200,000 chars)" value={bodyKo} maxLength={200000} rows={6}
          onChange={(e) => setBodyKo(e.target.value)} />
        <Field label="English contact (required · 200 chars)" value={contactEn} maxLength={200}
          onChange={(e) => setContactEn(e.target.value)} />
        <Field label="English hours (required · 200 chars)" value={hoursEn} maxLength={200}
          onChange={(e) => setHoursEn(e.target.value)} />
        <TextArea label="English guide text (required · 200,000 chars)" value={bodyEn} maxLength={200000} rows={6}
          onChange={(e) => setBodyEn(e.target.value)} />
        <DateTimeField label="Effective at (optional)" name="Effective at" value={effectiveAt} onChange={setEffectiveAt} />
        {badDate && <Notice tone="stop">The effective time cannot be read.</Notice>}
        <p className="text-xs text-slate-500">
          Saves as a new version. Old versions are not deleted. {WHERE}
        </p>
      </div>
    </Confirm>
  )
}
